import React, { useMemo, useState } from 'react';
import {
  createNewUTCDateFromString,
  useContextMenu,
  sortListOfObjectsByDateField,
  isDateInRange,
  useFetch,
  useApiClient,
} from '@marageti/z4-lib';
import { Partner, PartnerAvailability } from '@marageti/z4-sdk/lib/travel';
import { useParams } from 'react-router-dom';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { FilterOp, SearchRequest, SearchResult } from '@marageti/z4-sdk/lib/entity';
import AvailabilityView from './availability-view';
import { useAuthStore } from '../../../../store';
import { filterByDateMonthAndYear, getFirstDayOfCurrentMonth } from '../../../../utils/partners';

const AvailabilityContainer = () => {
  const apiClient = useApiClient();
  const { partnerId: currentPartnerId } = useParams();
  const [activeDate, setActiveDate] = useState<Date>(new Date());
  const [firstDayOfSelectedMonth, setFirstDayOfSelectedMonth] = useState(getFirstDayOfCurrentMonth());

  const {
    loggedInTeamMember,
  } = useAuthStore();

  // Only admins and partnerships team members can edit partner capacity
  const canEditCapacity = loggedInTeamMember?.userInfo?.some((info) => info.roles?.includes(RoleName.ADMIN) || info.roles?.includes(RoleName.PARTNERSHIPS));

  const {
    data: partner,
    error: partnerError,
    mutate: mutatePartner,
  } = useFetch<Partner>(apiClient.partnersClient.getPartner, [currentPartnerId], currentPartnerId !== '');

  const formattedDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return `${year}-${String(month + 1).padStart(2, '0')}-01`;
  };

  const searchRequest: SearchRequest = {
    filterBy: [{
      field: 'endDate',
      op: FilterOp.FilterOpGTE,
      value: [firstDayOfSelectedMonth],
    },
    {
      field: 'partnerId',
      op: FilterOp.FilterOpTerm,
      value: [currentPartnerId!],
    },
    ],
    sortBy: [{ field: 'createdAt', order: 'asc' }],
    pagination: { limit: 10000, page: 1 },
  };

  const {
    data: partnerAvailabilities,
    error: availabilitiesError,
  } = useFetch<SearchResult<PartnerAvailability>>(
    apiClient.partnersClient.postPartnerAvailabilitySearch,
    [searchRequest],
    partner?.id !== undefined,
  );

  const fetchHistoricAvailabilityData = () => {
    const firstDayOfMonth = new Date(Date.parse(firstDayOfSelectedMonth));
    const firstDayOfPreviousMonth = new Date(firstDayOfMonth.getFullYear(), firstDayOfMonth.getMonth(), 1);
    setFirstDayOfSelectedMonth(formattedDate(firstDayOfPreviousMonth));
  };
  const [editorOpen, setEditorOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor, contextMenuOpen] = useContextMenu();

  const partnerAvailabilityData = useMemo(() => {
    if (partnerAvailabilities && partnerAvailabilities.hits.length > 0) {
      return sortListOfObjectsByDateField(partnerAvailabilities.hits || [], 'startDate');
    }
    return undefined;
  }, [partnerAvailabilities]);

  const availabilityListByMonth = useMemo(() => partnerAvailabilityData && filterByDateMonthAndYear(partnerAvailabilityData, activeDate), [partnerAvailabilityData, activeDate]);

  /**
 * Checks if a lead limit is in effect for the current date (today).
 * If one or more lead limits are found to be overlapping, the function returns the lowest available leads per week value.
 * If no valid lead limit is found, the function returns null.
 *
 * Note: Availabilities with a `limitType` of "LIMIT_TRAVEL" are ignored.
 *
 * @param partnerAvailabilityData - The list of PartnerAvailability objects to search for a lead limit.
 * @returns The lowest leads per week value, or null if no valid lead limit is in effect.
 */
  const leadsPerWeekInRange = useMemo(() => {
    const today = new Date();
    const currentDate = createNewUTCDateFromString(today.toString());

    const availabilitiesInRange = partnerAvailabilityData?.filter((a) => {
      if (a.limitType === 'LIMIT_TRAVEL') {
        return false;
      }
      const startDate = createNewUTCDateFromString(a.startDate);
      const endDate = createNewUTCDateFromString(a.endDate);
      return isDateInRange(currentDate, startDate, endDate);
    }) || [];

    const availabilitiesWithLeads = availabilitiesInRange.filter((a) => a.leadsPerWeek != null);
    if (availabilitiesWithLeads.length === 0) return null;

    const lowestAvailability = availabilitiesWithLeads.reduce((lowest, a) => (lowest.leadsPerWeek <= a.leadsPerWeek ? lowest : a));
    return lowestAvailability.leadsPerWeek;
  }, [partnerAvailabilityData]);

  const handleOpenContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setContextMenuAnchor(e.currentTarget || null);
  };

  const handleCloseContextMenu = () => setContextMenuAnchor(null);

  const handleCloseEditor = () => {
    setEditorOpen(false);
    mutatePartner();
    handleCloseContextMenu();
  };

  const handleOpenEditor = () => setEditorOpen(true);

  const error = partnerError || availabilitiesError;

  const menuId = 'capacity-context-menu';

  const editContextMenu = (canEditCapacity) ? {
    id: menuId,
    items: [{
      label: 'Edit',
      action: handleOpenEditor,
    }],
  } : undefined;

  const handleActiveDateChange = (date: Date) => {
    setActiveDate(date);
  };

  return (
    <AvailabilityView
      contextMenuAnchor={contextMenuAnchor}
      contextMenuOpen={contextMenuOpen}
      editorOpen={editorOpen}
      error={error}
      onCloseContextMenu={handleCloseContextMenu}
      onCloseEditor={handleCloseEditor}
      onOpenContextMenu={handleOpenContextMenu}
      onOpenEditor={handleOpenEditor}
      partnerAvailability={partnerAvailabilityData}
      partnerAvailabilityList={availabilityListByMonth}
      capacity={partner?.capacity || 0}
      partnerId={partner?.id || ''}
      leadLimit={leadsPerWeekInRange !== null ? leadsPerWeekInRange : partner?.capacity || 0}
      fetchHistoricAvailabilityData={fetchHistoricAvailabilityData}
      canEditCapacity={canEditCapacity}
      contextMenuProps={editContextMenu}
      handleActiveDateChange={handleActiveDateChange}
      activeDate={activeDate}
    />
  );
};

export default AvailabilityContainer;
